import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { products, type Product } from '../data/products';

// Compras inventadas: nombre, ciudad y minutos salen al azar en cada aviso.
const NAMES = [
  'Laura M.', 'Carlos R.', 'Lucía G.', 'Javier P.', 'Marta S.', 'Daniel F.', 'Sara L.',
  'Pablo T.', 'Andrea V.', 'Álvaro N.', 'Paula C.', 'Sergio D.', 'Irene B.', 'Adrián H.',
  'Nerea O.', 'Raúl E.', 'Cristina A.', 'Iván J.', 'Elena M.', 'Hugo R.',
];
const CITIES = [
  'Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Málaga', 'Zaragoza', 'Bilbao', 'Alicante',
  'Murcia', 'Valladolid', 'Granada', 'Vigo', 'Córdoba', 'A Coruña', 'Santander', 'Gijón', 'Pamplona',
];

const VISIBLE_MS = 6_000;
const ENTER_MS = 1_200;
const FADE_MS = 1_200;
/** Como mucho tres avisos por visita, para que no se vuelva un goteo constante. */
const MAX_PER_VISIT = 3;
const COUNT_KEY = 'vapsilver-recent-purchases-shown';

const pick = <T,>(list: T[]) => list[Math.floor(Math.random() * list.length)];
const between = (min: number, max: number) => min + Math.random() * (max - min);

interface Purchase {
  name: string;
  city: string;
  minutes: number;
  product: Product;
  variant?: string;
}

function fakePurchase(): Purchase | null {
  const pool = products.filter((p) => p.availableForSale && p.image);
  if (!pool.length) return null;
  const product = pick(pool);
  return {
    name: pick(NAMES),
    city: pick(CITIES),
    minutes: Math.round(between(2, 48)),
    product,
    // `label` es siempre «Sabor»; el sabor concreto va en `value`.
    variant: product.variants?.length ? pick(product.variants).value : undefined,
  };
}

/** Aviso de «compra reciente», arriba a la derecha, que aparece de vez en cuando y se va solo. */
export default function RecentPurchaseToast() {
  const { pathname } = useLocation();
  // En el panel no pinta nada y en el checkout solo distraería.
  const muted = pathname === '/panel' || pathname === '/checkout';
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (muted) {
      setPurchase(null);
      return;
    }
    let timer: number;
    const later = (ms: number, fn: () => void) => (timer = window.setTimeout(fn, ms));

    const tick = () => {
      const shown = Number(sessionStorage.getItem(COUNT_KEY) ?? 0);
      if (shown >= MAX_PER_VISIT) return;
      // Ni detrás del control de edad ni con la pestaña en segundo plano.
      if (document.hidden || localStorage.getItem('vapsilver-age-verified') !== 'true') {
        return later(10_000, tick);
      }
      sessionStorage.setItem(COUNT_KEY, String(shown + 1));
      setLeaving(false);
      setPurchase(fakePurchase());
      later(VISIBLE_MS, () => {
        setLeaving(true);
        later(FADE_MS, () => {
          setPurchase(null);
          later(between(60_000, 150_000), tick);
        });
      });
    };

    later(between(15_000, 35_000), tick);
    return () => clearTimeout(timer);
  }, [muted]);

  if (!purchase) return null;
  const { name, city, minutes, product, variant } = purchase;

  return (
    // Justo por debajo del header (h-20 / lg:h-24) y por detrás de él (z-40),
    // para que el menú móvil y el carrito queden siempre encima.
    // La entrada y el desvanecimiento van en elementos distintos: fade-up rellena
    // `opacity: 1` al acabar (fill-mode both) y en el mismo nodo anularía el `opacity-0`.
    <div
      role="status"
      className={`fixed right-4 top-24 z-30 w-[min(21rem,calc(100vw-2rem))] transition-opacity sm:right-6 lg:top-28 ${
        leaving ? 'pointer-events-none opacity-0' : ''
      }`}
      style={{ transitionDuration: `${FADE_MS}ms` }}
    >
      <div
        className="relative flex items-center gap-3 overflow-hidden rounded-2xl border border-ink-600 bg-ink-900/95 p-3 pr-9 shadow-elev backdrop-blur motion-safe:animate-fade-up"
        style={{ animationDuration: `${ENTER_MS}ms` }}
      >
        {/* Cuenta atrás hasta que el aviso empieza a desvanecerse. */}
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-0.5 origin-left animate-shrink-x bg-vapor-400"
          style={{ animationDuration: `${VISIBLE_MS}ms` }}
        />
        <img src={product.image} alt="" className="h-12 w-12 shrink-0 rounded-xl bg-ink-800 object-cover" />
        <Link to={`/producto/${product.slug}`} onClick={() => setLeaving(true)} className="min-w-0 text-sm leading-snug">
          <span className="block text-[10px] font-medium uppercase tracking-widest text-vapor-400">Compra reciente</span>
          {/* Hay sabores combinados muy largos («Grape Burst + Lychee Ice + …»). */}
          <span className="line-clamp-3 text-bone-200">
            <span className="font-semibold text-bone-100">{name}</span> ha comprado{' '}
            <span className="font-semibold text-bone-100">
              {product.name}
              {variant && ` · ${variant}`}
            </span>
          </span>
          <span className="mt-0.5 block text-xs text-bone-400">
            {city} · hace {minutes} min
          </span>
        </Link>
        <button
          type="button"
          onClick={() => setLeaving(true)}
          aria-label="Cerrar aviso"
          className="absolute right-2.5 top-2 text-sm leading-none text-bone-400 hover:text-bone-100"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
